/**
 * Shared pure resolver for the "is a `not-found` master-plan state verified-legacy-safe to
 * render, or does it require an explicit no-plan / loading state?" question. Both
 * `useMasterPlanReadiness` (page-level UI banner) and `useCheckStowageIntegration` (effect-gating
 * used by useQueryParamNavigation and other Events data-loading effects) must agree on this
 * determination — if they drift, an effect could start loading rotation/event data for a status
 * the UI itself does not consider ready.
 *
 * Do NOT infer legacy readiness merely from `not-found` before voyage context is known: when a
 * stowage cutoff is configured for the vessel but the current voyage has not resolved yet, this
 * returns 'loading' rather than assuming legacy safety. Only a vessel with no stowage cutover
 * configured at all (`stowageCutoffVoyage == null`) is verified legacy without needing voyage
 * context, because such a vessel is never stowage-covered.
 */
export const resolveNotFoundPlanStatus = (
    stowageCutoffVoyage: number | string | null | undefined,
    currentVoyageNumber: number | string | null | undefined
): 'ready' | 'no-plan' | 'loading' => {
    if (stowageCutoffVoyage == null) {
        // No stowage cutover configured at all for this vessel — entirely pre-stowage/legacy,
        // verified safe without needing voyage context.
        return 'ready';
    }

    if (currentVoyageNumber == null) {
        // Voyage context not yet known — do not infer legacy readiness merely from not-found.
        return 'loading';
    }

    const isCurrentVoyageWithinStowageEra = Number(currentVoyageNumber) >= Number(stowageCutoffVoyage);
    return isCurrentVoyageWithinStowageEra ? 'no-plan' : 'ready';
};

export default resolveNotFoundPlanStatus;
